import { FixedSizeList as List } from 'react-window';
import type { Message } from '@unburden/common';
import { formatDistanceToNow } from 'date-fns';
import { authedUrl } from '../api';

const IMAGE_MIME_PREFIXES = ['image/'];

function isImage(mimeType: string): boolean {
  return IMAGE_MIME_PREFIXES.some(p => mimeType.startsWith(p));
}

interface VirtualizedMessagesProps {
  messages: Message[];
  height: number;
  loading: boolean;
}

const ITEM_SIZE = 145; // Approximate height of message in pixels, incl. attachment thumbnails

export function VirtualizedMessages({
  messages,
  height,
  loading,
}: VirtualizedMessagesProps) {
  const itemCount = loading ? messages.length + 1 : messages.length;

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (index >= messages.length) {
      return (
        <div style={style} className="flex items-center justify-center p-4">
          <p className="text-gray-500">Loading more messages...</p>
        </div>
      );
    }

    const message = messages[index];

    return (
      <div
        style={style}
        className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition"
      >
        <div className="flex items-baseline space-x-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {message.sender}
          </span>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {formatDistanceToNow(message.timestamp * 1000, { addSuffix: true })}
          </span>
        </div>
        <div className="mt-1 text-sm text-gray-800 break-words line-clamp-2">
          {message.content}
        </div>
        {message.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1 items-center">
            {message.attachments.slice(0, 4).map((attachment) =>
              isImage(attachment.mimeType) ? (
                <a
                  key={attachment.id}
                  href={authedUrl(`/api/attachments/${attachment.id}`)}
                  target="_blank"
                  rel="noreferrer"
                  title={attachment.filename}
                >
                  <img
                    src={authedUrl(`/api/attachments/${attachment.id}`)}
                    alt={attachment.filename}
                    loading="lazy"
                    className="h-14 w-14 object-cover rounded border border-gray-200"
                  />
                </a>
              ) : (
                <a
                  key={attachment.id}
                  href={authedUrl(`/api/attachments/${attachment.id}`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200"
                >
                  📎 {attachment.filename}
                </a>
              )
            )}
            {message.attachments.length > 4 && (
              <span className="text-xs text-gray-600">
                +{message.attachments.length - 4} more
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!messages.length && !loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No messages to display</p>
      </div>
    );
  }

  return (
    <List
      height={height}
      itemCount={itemCount}
      itemSize={ITEM_SIZE}
      width="100%"
      overscanCount={5}
    >
      {Row}
    </List>
  );
}
